import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { getCurrentYear } from "@freightsimple/time/helpers";
import { Link } from "@freightsimple/ui-core/Typography/Link";
import { PageFooter } from "@freightsimple/ui-core/PageLayout/PageFooter";
import { StickyFooterLayout } from "@freightsimple/ui-core/PageLayout/StickyFooterLayout";
import { T } from "@freightsimple/ui-core/Typography/T";

// The footer is app chrome (every page wraps in it), not a feature — so it
// lives in App/ alongside AppRouter. The bespoke layout lives in ui-core
// (PageFooter / StickyFooterLayout); this just supplies the content + route
// rules.
function DashboardFooter_() {
  const { pathname } = useLocation();
  // The Help Center link only shows on the signed-out auth screens (where the
  // top nav's Help Center isn't available).
  const showHelpCenterLink =
    pathname === "/sign-in" || pathname === "/create-account";

  return (
    <PageFooter>
      <T size="xs" color="muted">
        Copyright © FreightSimple Technologies, Inc. 2021-{getCurrentYear()}
      </T>
      <Link external href="https://www.freightsimple.com/terms-of-service">
        View Terms of Service
      </Link>
      <Link external href="https://www.freightsimple.com/privacy-policy">
        View Privacy Policy
      </Link>
      {showHelpCenterLink && (
        <Link external href="https://help.freightsimple.com">
          Help Center
        </Link>
      )}
    </PageFooter>
  );
}

/**
 * App-shell wrapper: pins the footer to the bottom on short pages and hides it
 * on the full-bleed flows (photo upload, auth screens).
 */
export function DashboardFooterLayout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const hideFooter =
    pathname === "/upload-photos" ||
    pathname === "/create-account" ||
    pathname === "/sign-in";

  return (
    <StickyFooterLayout footer={hideFooter ? undefined : <DashboardFooter_ />}>
      {children}
    </StickyFooterLayout>
  );
}
